import {
  useState,
  useRef,
  useEffect,
  useMemo,
  useLayoutEffect,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import {
  Editor,
  EditorState,
  Modifier,
  RichUtils,
  convertToRaw,
  ContentState,
  CompositeDecorator,
} from "draft-js";
import { convertToHTML, convertFromHTML } from "draft-convert";
import "draft-js/dist/Draft.css";
import PostToolbar from "../TribeTextEditor/PostToolbar";
import { api } from "../../actions/api";
import { toast } from "react-toastify";
import { useUpdateTribePost } from "../../Controllers/Tribe/TribePostController";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCreateMultiGroupPost,
  useGetTribeGroups,
} from "../../Controllers/Tribe/TribePostController";
import { filterTribeGroupByAccessLevel } from "../../helpers/TribeGroupeHelper";
import useUserStore from "../../zustand-stores/UserStore";
import {
  MENTION_ENTITY,
  mentionEntityStrategy,
  MentionSpan,
} from "../draftMention";
import { preserveExtraSpaces } from "../../Utils/preserveEditorWhitespace";

const chunkSize = 5 * 1024 * 1024; // 5MB per chunk

const mentionDecorator = new CompositeDecorator([
  { strategy: mentionEntityStrategy, component: MentionSpan },
]);

const getNodeAttr = (node, name) =>
  node.getAttribute?.(name) ?? node.attribs?.[name];
const getNodeClass = (node) => getNodeAttr(node, "class") || "";
const getNodeText = (node) =>
  node.textContent ?? (node.children || []).map((c) => c.data || "").join("");

const mentionConvertFromHTML = (html) =>
  convertFromHTML({
    htmlToEntity: (nodeName, node, createEntity) => {
      if (
        nodeName === "span" &&
        getNodeClass(node).includes("comment-mention")
      ) {
        const userId = getNodeAttr(node, "data-user-id") || "";
        const text = getNodeText(node);
        const fullName = text.startsWith("@") ? text.slice(1).trim() : text;
        return createEntity(MENTION_ENTITY, "IMMUTABLE", {
          userId: String(userId),
          fullName: fullName || "",
        });
      }
      return null;
    },
  })(html);

const CreatePostCard = ({
  groupId,
  onPostCreated,
  userProfile,
  members,
  isEditMode = false,
  editPostData = null,
  onPostUpdated = null,
  postOnMultipleGroup = false,
}) => {
  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty(mentionDecorator),
  );
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [taggedUser, setTaggedUser] = useState([]);

  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearchQuery, setMentionSearchQuery] = useState("");
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [effectiveMembersState, setEffectiveMembersState] = useState([]);
  const [dropdownPosition, setDropdownPosition] = useState({
    left: 0,
    width: 0,
    placement: "below",
    top: 0,
    bottom: 0,
    maxHeight: 220,
  });

  const editorContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const submitLockRef = useRef(false);
  const emojiPickerRef = useRef(null);
  const tagDropdownRef = useRef(null);
  const [progressMap, setProgressMap] = useState({});

  // scheduling state
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const base = scheduledDate ? new Date(scheduledDate) : new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  // edit mode state
  const [existingMedia, setExistingMedia] = useState([]);
  const [removedMediaIds, setRemovedMediaIds] = useState([]);
  const [originalTaggedUsers, setOriginalTaggedUsers] = useState([]);

  //tribe selection state
  const { user } = useUserStore();
  const tribeGroupDropdownRef = useRef(null);
  const [selectedTribeGroups, setSelectedTribeGroups] = useState([]);
  const [isTribeGroupDropdownOpen, setIsTribeGroupDropdownOpen] =
    useState(false);

  const queryClient = useQueryClient();
  const { mutate: updatePost, isPending: isUpdating } = useUpdateTribePost();

  const {
    data: tribeGroups = [],
    isLoading: tribeGroupsLoading,
    error: tribeGroupsError,
  } = useGetTribeGroups();

  const filteredTribeGroups = filterTribeGroupByAccessLevel(
    tribeGroups,
    user?.id,
  );

  const getSelectedTribeGroupsDisplayText = () => {
    if (selectedTribeGroups.length === 0)
      return tribeGroupsLoading ? "Loading groups..." : "Select Groups";

    if (selectedTribeGroups.length === 1) {
      const selected = filteredTribeGroups.find(
        (g) => String(g.id) === selectedTribeGroups[0],
      );
      return selected ? selected.name || selected.group_name : "Unknown";
    }

    return `${selectedTribeGroups.length} groups selected`;
  };

  const handleTribeGroupSelection = (groupId) => {
    const idStr = String(groupId);
    const isSelected = selectedTribeGroups.includes(idStr);

    if (isSelected) {
      setSelectedTribeGroups((prev) => prev.filter((id) => id !== idStr));
    } else {
      setSelectedTribeGroups((prev) => [...prev, idStr]);
    }
  };

  const { mutate: createMultiGroupPost, isPending: isPostingToTribe } =
    useCreateMultiGroupPost();

  // only posts in scheduled list (scheduled_post && !is_published) can edit schedule
  const isEditingScheduledPost =
    isEditMode &&
    editPostData?.scheduled_post &&
    editPostData.scheduled_post.is_published === false;

  function generateRandomString(length) {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < length; i += 1) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  const extractMediaIdFromUrl = (url) => {
    const match = url.match(/\.amazonaws\.com\/(\d+)\//);
    return match ? match[1] : null;
  };

  const buildMediaPreviewFromFiles = (files = []) => {
    return files.reduce(
      (acc, file) => {
        const previewUrl = URL.createObjectURL(file);
        if (file.type.startsWith("image/")) {
          acc.images.push(previewUrl);
        } else if (file.type.startsWith("video/")) {
          acc.videos.push(previewUrl);
        } else {
          acc.documents.push(previewUrl);
        }
        return acc;
      },
      { images: [], videos: [], documents: [] },
    );
  };

  const getRemovedTaggedUserIds = () => {
    if (originalTaggedUsers.length === 0) return [];
    return originalTaggedUsers.filter((userId) => !taggedUser.includes(userId));
  };

  const buildTaggedUsersPreview = (taggedIds = []) => {
    const previousTaggedUsers = editPostData?.tagged_users || [];
    return taggedIds
      .map((id) => {
        const fromMembers = effectiveMembersState.find(
          (m) => m.user?.id === id,
        )?.user;
        const fromPrevious = previousTaggedUsers.find((u) => u.id === id);
        return fromMembers || fromPrevious || null;
      })
      .filter(Boolean)
      .map((user) => ({
        id: user.id,
        full_name: user.full_name,
        image: user.image,
        slug: user.slug,
      }));
  };

  // Compute effective members: combine members from selected groups (when postOnMultipleGroup) or use prop
  useEffect(() => {
    if (!postOnMultipleGroup) {
      setEffectiveMembersState(members || []);
      return;
    }

    // If no groups are selected in multi-group mode, we show no members for tagging
    if (selectedTribeGroups.length === 0) {
      setEffectiveMembersState([]);
      return;
    }

    // Aggregate members from ONLY selected tribe groups, deduplicating by user id
    const memberMap = new Map();

    selectedTribeGroups.forEach((groupIdStr) => {
      const group = tribeGroups.find(
        (g) => String(g.id) === String(groupIdStr),
      );
      if (group?.members) {
        group.members.forEach((member) => {
          if (member.user?.id && !memberMap.has(member.user.id)) {
            memberMap.set(member.user.id, member);
          }
        });
      }
    });

    setEffectiveMembersState(Array.from(memberMap.values()));
  }, [postOnMultipleGroup, selectedTribeGroups, tribeGroups, members]);

  // Fetch members on-demand for mentions just like CommentInput
  useEffect(() => {
    if (!showMentionDropdown || !groupId || postOnMultipleGroup) return;

    let cancelled = false;

    (async () => {
      try {
        setIsLoadingMembers(true);
        const aboutRes = await api.get(`user/group/about/${groupId}`);
        const fetchedMembers = aboutRes?.data?.data?.about?.members || [];
        if (!cancelled) setEffectiveMembersState(fetchedMembers);
      } catch (e) {
        console.error("Failed to fetch group members", e);
      } finally {
        if (!cancelled) setIsLoadingMembers(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showMentionDropdown, groupId, postOnMultipleGroup]);

  // Fetch members on-demand for tag modal (bookmark icon), especially in feed/edit mode
  // where `members` prop may be empty or scoped to another context.
  useEffect(() => {
    if (!showTagModal || !groupId || postOnMultipleGroup) return;

    let cancelled = false;

    (async () => {
      try {
        setIsLoadingMembers(true);
        const aboutRes = await api.get(`user/group/about/${groupId}`);
        const fetchedMembers = aboutRes?.data?.data?.about?.members || [];
        if (!cancelled) setEffectiveMembersState(fetchedMembers);
      } catch (e) {
        console.error("Failed to fetch group members for tagging", e);
      } finally {
        if (!cancelled) setIsLoadingMembers(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showTagModal, groupId, postOnMultipleGroup]);

  // Proactively remove tagged users who are no longer in the effective members list
  // (e.g. because their group was unselected)
  useEffect(() => {
    if (postOnMultipleGroup && taggedUser.length > 0) {
      const availableIds = new Set(effectiveMembersState.map((m) => m.user.id));
      setTaggedUser((prev) => prev.filter((id) => availableIds.has(id)));
    }
  }, [effectiveMembersState, postOnMultipleGroup]);

  // init edit mode data
  useEffect(() => {
    if (!isEditMode || !editPostData) return;

    if (editPostData.text) {
      try {
        const contentState = mentionConvertFromHTML(editPostData.text);
        const newEditorState = EditorState.createWithContent(
          contentState,
          mentionDecorator,
        );
        setEditorState(newEditorState);
      } catch (error) {
        console.error("Error converting HTML to editor state:", error);
        setEditorState(EditorState.createEmpty(mentionDecorator));
      }
    }

    if (editPostData.tagged_users && editPostData.tagged_users.length > 0) {
      const userIds = editPostData.tagged_users.map((user) => user.id);
      setTaggedUser(userIds);
      setOriginalTaggedUsers(userIds);
    }

    if (editPostData.media) {
      const allExistingMedia = [
        ...(editPostData.media.images?.map((url, index) => ({
          id: extractMediaIdFromUrl(url) || `img_${index}`,
          type: "image",
          url,
          isExisting: true,
          name: `image_${index + 1}.jpg`,
        })) || []),
        ...(editPostData.media.videos?.map((url, index) => ({
          id: extractMediaIdFromUrl(url) || `vid_${index}`,
          type: "video",
          url,
          isExisting: true,
          name: `video_${index + 1}.mp4`,
        })) || []),
        ...(editPostData.media.documents?.map((url, index) => ({
          id: extractMediaIdFromUrl(url) || `doc_${index}`,
          type: "document",
          url,
          isExisting: true,
          name: `document_${index + 1}.pdf`,
        })) || []),
      ];
      setExistingMedia(allExistingMedia);
    }
  }, [isEditMode, editPostData]);

  useEffect(() => {
    if (!isEditingScheduledPost || !editPostData?.scheduled_post) return;

    const sp = editPostData.scheduled_post;

    let dateOnly;
    let timeOnly;

    const display = sp.scheduled_display_datetime;

    if (display) {
      const dt = new Date(display);

      if (!Number.isNaN(dt.getTime())) {
        const y = dt.getFullYear();
        const m = String(dt.getMonth() + 1).padStart(2, "0");
        const d = String(dt.getDate()).padStart(2, "0");

        const hh = String(dt.getHours()).padStart(2, "0");
        const mm = String(dt.getMinutes()).padStart(2, "0");

        if (!dateOnly) dateOnly = `${y}-${m}-${d}`;
        if (!timeOnly) timeOnly = `${hh}:${mm}`;
      }
    }

    if (dateOnly) setScheduledDate(dateOnly);
    if (timeOnly) setScheduledTime(timeOnly);
  }, [isEditingScheduledPost, editPostData]);

  // position calendar when modal opens
  useEffect(() => {
    if (!showScheduleModal) return;
    const base = scheduledDate ? new Date(scheduledDate) : new Date();
    setCalendarMonth(new Date(base.getFullYear(), base.getMonth(), 1));
  }, [showScheduleModal, scheduledDate]);

  const uploadFileInChunks = async (file, fileId) => {
    const totalChunks = Math.ceil(file.size / chunkSize);

    for (let chunkNumber = 1; chunkNumber <= totalChunks; chunkNumber += 1) {
      const start = (chunkNumber - 1) * chunkSize;
      const end = Math.min(file.size, start + chunkSize);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append("file_id", fileId);
      formData.append("chunk_number", chunkNumber);
      formData.append("total_chunks", totalChunks);
      formData.append("file_name", file.name);
      formData.append("chunk", chunk);

      await api.post("/upload-chunk", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setProgressMap((prev) => ({
        ...prev,
        [file.name]: Math.round((chunkNumber / totalChunks) * 100),
      }));
    }
  };

  const pad2 = (n) => String(n).padStart(2, "0");

  const getNowLocal = () => new Date();

  const toHHMM = (d) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

  const isPastScheduledDateTime = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return false;

    const [y, m, d] = dateStr.split("-").map(Number);
    const [hh, mm] = timeStr.split(":").map(Number);

    const scheduled = new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0);
    const nowLocal = getNowLocal();

    return scheduled.getTime() < nowLocal.getTime();
  };

  const handleSubmitPost = async () => {
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    let releaseOnSettled = false;
    try {
      setIsSubmitting(true);
      const contentState = editorState.getCurrentContent();

      const mentionedUserIds = [];
      contentState.getBlocksAsArray().forEach((block) => {
        block.findEntityRanges(
          (char) => {
            const key = char.getEntity();
            return (
              key !== null &&
              contentState.getEntity(key).getType() === MENTION_ENTITY
            );
          },
          (start) => {
            const key = block.getEntityAt(start);
            const { userId } = contentState.getEntity(key).getData();
            if (userId && !mentionedUserIds.includes(userId)) {
              mentionedUserIds.push(userId);
            }
          },
        );
      });

      const rawEditorHtml = convertToHTML({
        blockToHTML: (block) => {
          if (block.type === "unstyled" && block.text.length === 0) {
            return (
              <p>
                <br />
              </p>
            );
          }
          return null;
        },
        entityToHTML: (entity, originalText) => {
          if (entity.type === MENTION_ENTITY && entity.data?.userId) {
            const displayText =
              originalText || `@${entity.data.fullName || ""}`;
            return `<span class="comment-mention" data-user-id="${entity.data.userId}">${displayText}</span>`;
          }
          return originalText;
        },
      })(contentState);

      const text = preserveExtraSpaces(rawEditorHtml);

      // 2) Helpers
      const appendScheduleFields = (fd) => {
        if (!scheduledDate || !scheduledTime) return;

        fd.append("scheduled_for_date", scheduledDate);
        const normalizedTime =
          scheduledTime.length === 5 ? `${scheduledTime}:00` : scheduledTime;
        fd.append("scheduled_for_time", normalizedTime);
        fd.append("is_scheduled", 1);
      };

      const appendTaggedUsers = (fd) => {
        if (!taggedUser?.length) return;
        taggedUser.forEach((userId) => fd.append("tagged_users[]", userId));
      };

      const appendMentionedUsers = (fd) => {
        if (!mentionedUserIds?.length) return;
        mentionedUserIds.forEach((userId, index) =>
          fd.append(`mentioned_users[${index}]`, userId),
        );
      };

      const appendGroupId = (fd) => {
        if (!groupId) return;
        fd.append("group_id", groupId);
      };

      const appendMultipleGroupIds = (fd) => {
        if (!selectedTribeGroups?.length) return;

        selectedTribeGroups.forEach((groupId, index) => {
          fd.append(`group_ids[${index}]`, groupId);
        });
      };

      const appendMediaForCreate = async (fd) => {
        if (!selectedFiles?.length) return;

        const uploadedVideoIds = [];

        // eslint-disable-next-line no-restricted-syntax
        for (const file of selectedFiles) {
          if (file.type.startsWith("video")) {
            const fileId = generateRandomString(10);
            // eslint-disable-next-line no-await-in-loop
            await uploadFileInChunks(file, fileId);
            uploadedVideoIds.push(fileId);
          } else {
            fd.append("media[]", file);
          }
        }

        if (uploadedVideoIds.length > 0) {
          fd.append("videoIds", JSON.stringify(uploadedVideoIds));
        }
      };

      const appendMediaForEdit = (fd) => {
        if (!selectedFiles?.length) return;
        selectedFiles.forEach((file) => fd.append("media[]", file));
      };

      // 3) EDIT flow
      if (isEditMode) {
        const formDataForEdit = new FormData();
        formDataForEdit.append("text", text);
        formDataForEdit.append("_method", "PUT");

        appendScheduleFields(formDataForEdit);
        appendMediaForEdit(formDataForEdit);

        if (removedMediaIds?.length) {
          removedMediaIds.forEach((id) =>
            formDataForEdit.append("removed_media_ids[]", id),
          );
        }

        appendTaggedUsers(formDataForEdit);
        appendMentionedUsers(formDataForEdit);

        const removedTaggedUserIds = getRemovedTaggedUserIds();
        if (removedTaggedUserIds?.length) {
          removedTaggedUserIds.forEach((userId) =>
            formDataForEdit.append("remove_tagged_users[]", userId),
          );
        }

        appendGroupId(formDataForEdit);

        updatePost(
          { formData: formDataForEdit, postId: editPostData.id },
          {
            onSuccess: () => {
              const newMediaPreviews =
                buildMediaPreviewFromFiles(selectedFiles);
              const taggedUsersPreview = buildTaggedUsersPreview(taggedUser);
              const updatedPost = {
                ...editPostData,
                text,
                tagged_users: taggedUsersPreview,
                media: {
                  videos: existingMedia
                    .filter(
                      (m) =>
                        m.type === "video" && !removedMediaIds.includes(m.id),
                    )
                    .map((m) => m.url)
                    .concat(newMediaPreviews.videos),
                  documents: existingMedia
                    .filter(
                      (m) =>
                        m.type === "document" &&
                        !removedMediaIds.includes(m.id),
                    )
                    .map((m) => m.url)
                    .concat(newMediaPreviews.documents),
                  images: existingMedia
                    .filter(
                      (m) =>
                        m.type === "image" && !removedMediaIds.includes(m.id),
                    )
                    .map((m) => m.url)
                    .concat(newMediaPreviews.images),
                },
              };

              if (onPostUpdated) {
                onPostUpdated(updatedPost);
              }
              queryClient.invalidateQueries({ queryKey: ["scheduledPosts"] });
            },
            onError: () => {
              toast.error("Failed to update post. Please try again.");
            },
          },
        );

        setTaggedUser([]);
        setShowTagModal(false);
        setIsTribeGroupDropdownOpen(false);
        setSelectedFiles([]);
        return;
      }

      // 4) CREATE payload (single or multi)
      const formDataForCreate = new FormData();
      formDataForCreate.append("text", text);

      appendScheduleFields(formDataForCreate);
      appendGroupId(formDataForCreate);
      appendTaggedUsers(formDataForCreate);
      appendMentionedUsers(formDataForCreate);

      if (selectedFiles?.length) {
        try {
          await appendMediaForCreate(formDataForCreate);
        } catch (e) {
          console.log("error on chunking");
        }
      }

      // 5) CREATE request (multi group vs single group)
      if (postOnMultipleGroup) {
        appendMultipleGroupIds(formDataForCreate);
        releaseOnSettled = true;
        createMultiGroupPost(formDataForCreate, {
          onSettled: () => {
            submitLockRef.current = false;
          },
          onSuccess: (res) => {
            const postsData = res?.posts;

            if (onPostCreated && Array.isArray(postsData) && postsData.length) {
              const newMediaPreviews =
                buildMediaPreviewFromFiles(selectedFiles);
              const taggedUsersPreview = buildTaggedUsersPreview(taggedUser);
              const newPosts = postsData.map((post) => ({
                ...post,
                id: post.id || Date.now(),
                text: post.text || text,
                created_at: new Date().toISOString(),
                likes_count: 0,
                created_by: {
                  id: post.user_id || "current-user-id",
                  full_name: userProfile.full_name || "Current User",
                  image: userProfile.image || "default-avatar.png",
                },
                tagged_users:
                  post.tagged_users?.length > 0
                    ? post.tagged_users
                    : taggedUsersPreview,
                media: newMediaPreviews,
              }));

              newPosts.forEach((p) => onPostCreated(p));
            }

            queryClient.invalidateQueries({ queryKey: ["scheduledPosts"] });

            setScheduledTime("");
            setTaggedUser([]);
            setShowTagModal(false);
            setSelectedTribeGroups([]);
            setIsTribeGroupDropdownOpen(false);

            toast.success("Post created successfully!");
            setEditorState(EditorState.createEmpty(mentionDecorator));
            setSelectedFiles([]);
          },
          onError: (err) => {
            // 422 lands here
            const msg =
              err?.data?.message ||
              (err?.status === 422
                ? "Validation error. Please check your inputs."
                : null) ||
              "Failed to create post. Please try again.";

            toast.error(msg);
          },
        });

        return;
      } else {
        const response = await api.post(
          "/user/create-post",
          formDataForCreate,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );

        if (onPostCreated && response.data?.data?.post) {
          const newMediaPreviews = buildMediaPreviewFromFiles(selectedFiles);
          const taggedUsersPreview = buildTaggedUsersPreview(taggedUser);
          const newPost = {
            ...response.data.data.post,
            id: response.data.data.post.id || Date.now(),
            text: response.data.data.post.text || text,
            created_at: new Date().toISOString(),
            likes_count: 0,
            created_by: {
              id: response.data.data.post.user_id || "current-user-id",
              full_name: userProfile.full_name || "Current User",
              image: userProfile.image || "default-avatar.png",
            },
            tagged_users:
              response.data.data.post.tagged_users?.length > 0
                ? response.data.data.post.tagged_users
                : taggedUsersPreview,
            media: newMediaPreviews,
          };
          onPostCreated(newPost);
        }
        toast.success("Post created successfully!");
        queryClient.invalidateQueries({ queryKey: ["scheduledPosts"] });
      }

      // 6) CREATE UI cleanup
      setTaggedUser([]);
      setShowTagModal(false);
      setSelectedTribeGroups([]);
      setSelectedFiles([]);
      setIsTribeGroupDropdownOpen(false);

      setEditorState(EditorState.createEmpty(mentionDecorator));
      setSelectedFiles([]);
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} post:`,
        error,
      );
      toast.error(
        `Failed to ${isEditMode ? "update" : "create"} post. Please try again.`,
      );
    } finally {
      setIsSubmitting(false);
      if (!releaseOnSettled) submitLockRef.current = false;
    }
  };

  const handleInlineStyle = (style) => {
    setEditorState(RichUtils.toggleInlineStyle(editorState, style));
  };

  const handleBlockType = (blockType) => {
    setEditorState(RichUtils.toggleBlockType(editorState, blockType));
  };

  const handleExpandEditor = () => {
    setIsExpanded((prev) => {
      const next = !prev;
      if (next) {
        setTimeout(() => {
          focusPostEditor();
        }, 100);
      }
      return next;
    });
  };

  const focusPostEditor = () => {
    const contentEditable = editorContainerRef.current?.querySelector(
      ".public-DraftEditor-content",
    );
    contentEditable?.focus?.();
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleTagUser = () => {
    // In feed/multi-group post flow, tagging depends on selected group members.
    if (postOnMultipleGroup && selectedTribeGroups.length === 0) {
      toast.info("Select a group first to tag users.");
      return;
    }
    setShowTagModal((prev) => !prev);
  };

  const recomputeDropdownPosition = () => {
    if (!editorContainerRef.current) return;
    const rect = editorContainerRef.current.getBoundingClientRect();
    const viewportW = window.innerWidth || document.documentElement.clientWidth;
    const viewportH =
      window.innerHeight || document.documentElement.clientHeight;

    const horizontalPadding = 8;
    const desiredWidth = Math.max(rect.width - 32, 200);
    const maxWidth = Math.max(200, viewportW - horizontalPadding * 2);
    const width = Math.min(desiredWidth, maxWidth);

    const spaceBelow = viewportH - rect.bottom;
    const spaceAbove = rect.top;
    const desiredMaxHeight = 260;

    let placement = "below";
    if (spaceBelow >= 160) {
      placement = "below";
    } else if (spaceAbove >= spaceBelow) {
      placement = "above";
    } else {
      placement = "below";
    }

    let top = 0;
    let bottom = 0;
    let maxHeight = desiredMaxHeight;

    if (placement === "below") {
      top = rect.bottom + 8;
      maxHeight = Math.max(
        140,
        Math.min(desiredMaxHeight, viewportH - top - 8),
      );
    } else {
      bottom = viewportH - rect.top + 8;
      maxHeight = Math.max(140, Math.min(desiredMaxHeight, spaceAbove - 16));
    }

    let left = rect.left;
    if (left + width + horizontalPadding > viewportW) {
      left = Math.max(horizontalPadding, viewportW - width - horizontalPadding);
    }
    left = Math.max(horizontalPadding, left);

    setDropdownPosition({
      left,
      width,
      placement,
      top,
      bottom,
      maxHeight,
    });
  };

  useLayoutEffect(() => {
    if (!showMentionDropdown) return;
    recomputeDropdownPosition();
    const onWin = () => recomputeDropdownPosition();
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);
    return () => {
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
    };
  }, [showMentionDropdown]);

  const handleEditorChange = (newEditorState) => {
    setEditorState(newEditorState);
    const selection = newEditorState.getSelection();
    const blockKey = selection.getStartKey();
    const content = newEditorState.getCurrentContent();
    const block = content.getBlockForKey(blockKey);
    const text = block.getText();
    const offset = selection.getStartOffset();
    const textUpToCursor = text.slice(0, offset);
    const lastAtIndex = textUpToCursor.lastIndexOf("@");

    if (lastAtIndex === -1) {
      setShowMentionDropdown(false);
      setMentionSearchQuery("");
      return;
    }

    const charList = block.getCharacterList();
    const charAtAt = charList.get(lastAtIndex);
    const entityKeyAtAt = charAtAt?.getEntity();
    const atIsInsideMention =
      entityKeyAtAt &&
      content.getEntity(entityKeyAtAt).getType() === MENTION_ENTITY;

    if (atIsInsideMention) {
      setShowMentionDropdown(false);
      setMentionSearchQuery("");
      return;
    }

    const textAfterAt = textUpToCursor.slice(lastAtIndex + 1);
    const isActiveQuery = /^[\w]*$/.test(textAfterAt);

    if (isActiveQuery) {
      setShowMentionDropdown(true);
      setMentionSearchQuery(textAfterAt.toLowerCase());
    } else {
      setShowMentionDropdown(false);
      setMentionSearchQuery("");
    }
  };

  const getAlreadyMentionedUserIds = (contentState) => {
    const ids = new Set();
    contentState.getBlocksAsArray().forEach((block) => {
      block.findEntityRanges(
        (char) => {
          const key = char.getEntity();
          return (
            key !== null &&
            contentState.getEntity(key).getType() === MENTION_ENTITY
          );
        },
        (start) => {
          const key = block.getEntityAt(start);
          const data = contentState.getEntity(key).getData();
          if (data?.userId) ids.add(String(data.userId));
        },
      );
    });
    return ids;
  };

  const insertMention = (member) => {
    const content = editorState.getCurrentContent();
    const alreadyMentioned = getAlreadyMentionedUserIds(content);
    const targetId = String(member.user.id);
    if (alreadyMentioned.has(targetId)) {
      setShowMentionDropdown(false);
      setMentionSearchQuery("");
      return;
    }

    const selection = editorState.getSelection();
    const blockKey = selection.getStartKey();
    const block = content.getBlockForKey(blockKey);
    const text = block.getText();
    const offset = selection.getStartOffset();
    const textUpToCursor = text.slice(0, offset);
    const lastAtIndex = textUpToCursor.lastIndexOf("@");
    const startOffset = lastAtIndex >= 0 ? lastAtIndex : offset;

    const mentionLabel = `@${member.user.full_name}`;
    const contentWithEntity = content.createEntity(
      MENTION_ENTITY,
      "IMMUTABLE",
      { userId: targetId, fullName: member.user.full_name || "" },
    );
    const entityKey = contentWithEntity.getLastCreatedEntityKey();

    const mentionSelection = selection.merge({
      anchorKey: blockKey,
      anchorOffset: startOffset,
      focusKey: blockKey,
      focusOffset: offset,
    });

    const contentWithMention = Modifier.replaceText(
      contentWithEntity,
      mentionSelection,
      mentionLabel,
      null,
      entityKey,
    );

    const afterMentionOffset = startOffset + mentionLabel.length;
    const selectionAfterMention = mentionSelection.merge({
      anchorOffset: afterMentionOffset,
      focusOffset: afterMentionOffset,
    });

    const contentWithSpace = Modifier.insertText(
      contentWithMention,
      selectionAfterMention,
      " ",
      null,
      null,
    );

    setEditorState(
      EditorState.push(editorState, contentWithSpace, "insert-characters"),
    );
    setShowMentionDropdown(false);
    setMentionSearchQuery("");
  };

  const handleTagUserClicked = (userId) => {
    setTaggedUser((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
    setShowTagModal(false);
  };

  const handleMentionClicked = (member) => {
    insertMention(member);
  };

  const toggleEmojiPicker = () => {
    setShowEmojiPicker((prev) => !prev);
  };

  const handleEmojiSelect = (event) => {
    event.stopPropagation();
    const emoji = event.detail.unicode;

    setEditorState((prev) => {
      const contentState = prev.getCurrentContent();
      const selectionState = prev.getSelection();

      const newContentState = Modifier.insertText(
        contentState,
        selectionState,
        emoji,
      );

      return EditorState.push(prev, newContentState, "insert-characters");
    });
  };

  useEffect(() => {
    const handleClickOutsideTribeGroups = (event) => {
      if (
        isTribeGroupDropdownOpen &&
        tribeGroupDropdownRef.current &&
        !tribeGroupDropdownRef.current.contains(event.target)
      ) {
        setIsTribeGroupDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutsideTribeGroups);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideTribeGroups);
    };
  }, [isTribeGroupDropdownOpen]);

  useEffect(() => {
    if (!document.querySelector('script[src*="emoji-picker-element"]')) {
      const script = document.createElement("script");
      script.src =
        "https://cdn.jsdelivr.net/npm/emoji-picker-element@^1/index.js";
      script.type = "module";
      document.head.appendChild(script);
    }
  }, []);

  useEffect(() => {
    if (!showEmojiPicker || !emojiPickerRef.current) return;

    const emojiPickerElement = emojiPickerRef.current;

    const timer = setTimeout(() => {
      if (!emojiPickerElement.shadowRoot) return;

      if (
        !emojiPickerElement.shadowRoot.querySelector(
          "[data-emoji-picker-theme]",
        )
      ) {
        const style = document.createElement("style");
        style.setAttribute("data-emoji-picker-theme", "");
        style.textContent = `
          .picker { border-radius: 20px; background-color: #121e29; } 
          .pad-top { background-color: #121e29; } 
          input.search {
            background-color: #121e29;
            color: #ffffff;
          }
          input.search::placeholder {
            color: rgba(255, 255, 255, 0.55);
          }
          .skintone-button-wrapper { background-color: #121e29; }
        `;
        emojiPickerElement.shadowRoot.appendChild(style);
      }

      emojiPickerElement.addEventListener("emoji-click", handleEmojiSelect);
      emojiPickerElement.shadowRoot.querySelector("input.search")?.focus();
    }, 100);

    return () => {
      clearTimeout(timer);
      if (!emojiPickerElement) return;
      emojiPickerElement.removeEventListener("emoji-click", handleEmojiSelect);
    };
  }, [showEmojiPicker]);

  const removeTaggedMember = (userId) => {
    setTaggedUser((prev) => prev.filter((id) => id !== userId));
  };

  const getTaggedUserDetails = (userId) => {
    const member = effectiveMembersState.find((m) => m.user.id === userId);
    if (member) return member.user;

    // In feed/edit flows members can load asynchronously; fall back to
    // already-tagged user payload so chips render immediately.
    const fallbackUser = (editPostData?.tagged_users || []).find(
      (u) => u.id === userId,
    );
    return fallbackUser || null;
  };

  const handleRemoveExistingMedia = (mediaId) => {
    setExistingMedia((prev) => prev.filter((media) => media.id !== mediaId));
    setRemovedMediaIds((prev) => [...prev, mediaId]);
  };

  const handleRemoveNewFile = (fileIndex) => {
    setSelectedFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== fileIndex),
    );
  };

  useEffect(() => {
    const handleClickOutsideEmoji = (event) => {
      if (
        showEmojiPicker &&
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target) &&
        event.target.id !== "emojiButton"
      ) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutsideEmoji);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideEmoji);
    };
  }, [showEmojiPicker]);

  useEffect(() => {
    const handleClickOutsideTag = (event) => {
      if (!showTagModal || !tagDropdownRef.current) return;
      // Let the tag button onClick toggle closed; mousedown would run first and fight the toggle.
      if (event.target?.closest?.("#post-toolbar-tag-members")) return;
      if (!tagDropdownRef.current.contains(event.target)) {
        setShowTagModal(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutsideTag);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideTag);
    };
  }, [showTagModal]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
    }
  };

  const renderFilePreviews = () => {
    const allMedia = [
      ...existingMedia,
      ...selectedFiles.map((file, index) => ({
        id: `new_${index}`,
        type: file.type.startsWith("image/")
          ? "image"
          : file.type.startsWith("video/")
            ? "video"
            : "document",
        url: URL.createObjectURL(file),
        name: file.name,
        isExisting: false,
        file,
      })),
    ];

    if (allMedia.length === 0) return null;

    return (
      <div
        style={{
          marginTop: "10px",
          display: "flex",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        {allMedia.map((media, index) => (
          <div key={media.id} style={{ position: "relative" }}>
            {media.type === "image" ? (
              <img
                src={media.url}
                alt={`Preview ${index}`}
                style={{
                  width: "100px",
                  height: "100px",
                  objectFit: "cover",
                  borderRadius: "8px",
                }}
              />
            ) : media.type === "video" ? (
              <div
                style={{
                  width: "150px",
                  height: "100px",
                  borderRadius: "8px",
                  overflow: "hidden",
                  position: "relative",
                  background: "#2A4052",
                }}
              >
                <video
                  src={media.url}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  controls
                  preload="metadata"
                />
                <div
                  style={{
                    position: "absolute",
                    bottom: "4px",
                    left: "4px",
                    right: "4px",
                    background: "rgba(0, 0, 0, 0.7)",
                    color: "white",
                    fontSize: "10px",
                    padding: "2px 4px",
                    borderRadius: "4px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {media.name}
                </div>
              </div>
            ) : (
              <div
                style={{
                  width: "100px",
                  height: "100px",
                  background: "#2A4052",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  padding: "8px",
                }}
              >
                <div
                  style={{
                    color: "#fff",
                    fontSize: "12px",
                    textAlign: "center",
                    wordBreak: "break-word",
                  }}
                >
                  {media.name}
                </div>
                <div
                  style={{ color: "#888", fontSize: "10px", marginTop: "4px" }}
                >
                  {media.type}
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={() => {
                if (media.isExisting) {
                  handleRemoveExistingMedia(media.id);
                } else {
                  const fileIndex = selectedFiles.findIndex(
                    (f) => f === media.file,
                  );
                  handleRemoveNewFile(fileIndex);
                }
              }}
              style={{
                position: "absolute",
                top: "-8px",
                right: "-8px",
                background: "#ff4d4f",
                color: "white",
                border: "none",
                borderRadius: "50%",
                width: "20px",
                height: "20px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              ×
            </button>

            {media.isExisting && (
              <div
                style={{
                  position: "absolute",
                  top: "4px",
                  left: "4px",
                  background: "rgba(0, 140, 255, 0.8)",
                  color: "white",
                  fontSize: "8px",
                  padding: "2px 4px",
                  borderRadius: "4px",
                }}
              >
                EXISTING
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const canSubmit =
    !isSubmitting &&
    !isPostingToTribe &&
    !isUpdating &&
    (editorState.getCurrentContent().hasText() || selectedFiles.length > 0);

  // current-date/time helpers for time min
  const now = getNowLocal();
  const todayIso = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
  const currentTimeHHMM = toHHMM(now);
  const timeMin = scheduledDate === todayIso ? currentTimeHHMM : "00:00";

  const openScheduleModal = () => {
    if (!canSubmit) return;

    if (!scheduledDate) {
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const d = String(now.getDate()).padStart(2, "0");
      setScheduledDate(`${y}-${m}-${d}`);
    }

    if (!scheduledTime) {
      setScheduledTime(currentTimeHHMM);
    }

    setShowScheduleModal(true);
  };

  const closeScheduleModal = () => {
    setShowScheduleModal(false);
  };

  useEffect(() => {
    if (!scheduledDate || !scheduledTime) return;
    if (scheduledDate === todayIso && scheduledTime < currentTimeHHMM) {
      setScheduledTime(currentTimeHHMM);
    }
  }, [scheduledDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScheduleConfirm = async () => {
    if (!scheduledDate || !scheduledTime) {
      toast.error("Please select both date and time for scheduling.");
      return;
    }

    if (isPastScheduledDateTime(scheduledDate, scheduledTime)) {
      toast.error("Scheduled time cannot be in the past.");
      return;
    }

    await handleSubmitPost();
    setShowScheduleModal(false);
  };
  return (
    <div>
      <div
        className="journal-card"
        style={{
          backgroundColor: "#1C2A37",
          borderRadius: "24px",
          marginLeft: "24px",
          marginRight: "24px",
          marginBottom: "31px",
          padding: "20px",
          position: "relative",
        }}
      >
        <div
          className="d-flex gap-1 align-items-center"
          style={{ marginTop: "-14px" }}
        >
          <img
            src={userProfile?.image || "icons/james.svg"}
            alt="User"
            style={{ width: "28px", height: "28px", borderRadius: "50%" }}
          />

          <PostToolbar
            editorState={editorState}
            onToggleInlineStyle={handleInlineStyle}
            onToggleBlockType={handleBlockType}
            onExpandEditor={handleExpandEditor}
            onFileSelect={handleFileSelect}
            showEmojiPicker={showEmojiPicker}
            emojiPickerRef={emojiPickerRef}
            onEmojiSelect={toggleEmojiPicker}
            isExpanded={isExpanded}
            onBookmarkIcon={handleTagUser}
            showTagModal={showTagModal}
            members={effectiveMembersState}
            isLoadingMembers={isLoadingMembers}
            handleTagUserClicked={handleTagUserClicked}
            userProfile={userProfile}
            tagDropdownRef={tagDropdownRef}
          />

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: "none" }}
            multiple
          />

          <div
            className="d-flex gap-4 ms-auto"
            style={{ alignItems: "center" }}
          >
            {postOnMultipleGroup && (
              <div className="mb-3">
                <div
                  className="chat-container"
                  style={{ padding: "0px", height: "auto", overflowY: "unset" }}
                >
                  <div
                    className="textfield-1 position-relative"
                    ref={tribeGroupDropdownRef}
                    style={{ borderColor: "#364756" }}
                  >
                    <input
                      type="text"
                      className="custom-select text-body-b2"
                      placeholder=""
                      value={getSelectedTribeGroupsDisplayText()}
                      onClick={() =>
                        setIsTribeGroupDropdownOpen(!isTribeGroupDropdownOpen)
                      }
                      readOnly
                      disabled={tribeGroupsLoading}
                      style={{
                        opacity: tribeGroupsLoading ? 0.6 : 1,
                        borderColor: "#364756",
                      }}
                    />

                    {isTribeGroupDropdownOpen && (
                      <div
                        className="custom-select-dropdown"
                        style={{
                          zIndex: 10,
                          display: "block",
                          position: "absolute",
                          width: "100%",
                          background: "#192f41",
                          border: "1px solid #2d3c48",
                          borderRadius: "20px",
                          padding: "16px 20px",
                          boxShadow: "2px 4px 6px 0px #0000002e",
                          maxHeight: "300px",
                          overflowY: "auto",
                        }}
                      >
                        <h5
                          className="text-body-b2 mb-2"
                          style={{ color: "#E6ECF1", fontWeight: "500" }}
                        >
                          Groups List
                        </h5>

                        {filteredTribeGroups.map((group) => (
                          <div
                            key={group.id}
                            className="d-flex align-items-center mb-2"
                            style={{ cursor: "pointer", gap: "12px" }}
                            onClick={() => handleTribeGroupSelection(group.id)}
                          >
                            <div
                              style={{
                                width: "20px",
                                height: "20px",
                                border: selectedTribeGroups.includes(
                                  String(group.id),
                                )
                                  ? "2px solid #00B4FF"
                                  : "2px solid #2d3c48",
                                borderRadius: "4px",
                                backgroundColor: selectedTribeGroups.includes(
                                  String(group.id),
                                )
                                  ? "#00B4FF"
                                  : "transparent",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "all 0.2s ease",
                                flexShrink: 0,
                              }}
                            >
                              {selectedTribeGroups.includes(
                                String(group.id),
                              ) && (
                                <svg
                                  width="12"
                                  height="9"
                                  viewBox="0 0 12 9"
                                  fill="none"
                                  style={{ color: "white" }}
                                >
                                  <path
                                    d="M1 4.5L4.5 8L11 1.5"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </div>

                            <span className="body-1-default label-san-target text-truncate-ellipsis">
                              {group.name || group.group_name}
                            </span>
                          </div>
                        ))}

                        {filteredTribeGroups.length === 0 &&
                          !tribeGroupsLoading && (
                            <span className="text-body-b2 text-muted">
                              No groups found
                            </span>
                          )}
                      </div>
                    )}

                    <label
                      className="text-body-b2"
                      style={{ backgroundColor: "#132534" }}
                    >
                      Select Groups
                    </label>
                  </div>
                </div>
              </div>
            )}

            <button
              type="button"
              className="chat-all-btn"
              style={{
                width: "120px",
                height: "36px",
                whiteSpace: "nowrap",
                backgroundColor: "#008CFF",
                color: "white",
              }}
              onClick={handleSubmitPost}
              disabled={!canSubmit}
            >
              {isSubmitting || isPostingToTribe || isUpdating ? (
                <span>{isEditMode ? "Updating..." : "Posting..."}</span>
              ) : (
                <div style={{ marginLeft: "-10px" }}>
                  <span style={{ marginLeft: "-10px", marginRight: "8px" }}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clipPath="url(#clip0_57395_25770)">
                        <path
                          d="M12.6668 8.66732H8.66683V12.66732H7.3335V8.66732H3.3335V7.33398H7.3335V3.33398H8.66683V7.33398H12.6668V8.66732Z"
                          fill="#F2F0FF"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_57395_25770">
                          <rect width="16" height="16" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </span>
                  <span>{isEditMode ? "Update" : "Post"}</span>
                </div>
              )}
            </button>
          </div>
        </div>

        <hr
          style={{
            borderTop: "1px solid var(--Bg-Border, #2A4052)",
            opacity: 1,
            width: "calc(100% + 40px)",
            marginLeft: "-20px",
            marginRight: "-20px",
            marginTop: "6px",
            marginBottom: "18px",
          }}
        />

        {isSubmitting || isPostingToTribe || isUpdating ? (
          "Processing"
        ) : (
          <>
            <div
              className="editor-container commentEditor"
              ref={editorContainerRef}
              style={{
                padding: "8px 0px",
                borderRadius: "8px",
                color: "#C8CED3",
                cursor: "text",
                wordBreak: "break-word",
                transition: "height 0.3s ease",
                position: "relative",
              }}
              onClick={() => {
                focusPostEditor();
              }}
            >
              {taggedUser.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    marginBottom: "12px",
                  }}
                >
                  {taggedUser.map((userId) => {
                    const taggedUserData = getTaggedUserDetails(userId);
                    if (!taggedUserData) return null;

                    return (
                      <div
                        key={taggedUserData.id}
                        className="member-chip"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          backgroundColor: "#2a4052",
                          padding: "6px 12px",
                          borderRadius: "20px",
                          width: "fit-content",
                        }}
                      >
                        <img
                          src={
                            taggedUserData.image || "/icons/default-user.svg"
                          }
                          alt={taggedUserData.full_name}
                          style={{
                            width: "24px",
                            height: "24px",
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                        <span style={{ fontSize: "14px" }}>
                          {taggedUserData.slug
                            ? `@${taggedUserData.slug}`
                            : taggedUserData.full_name ||
                              "User " + taggedUserData.id}
                        </span>
                        <img
                          src="icons/cross.svg"
                          style={{
                            width: "12px",
                            height: "12px",
                            cursor: "pointer",
                            marginLeft: "4px",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            removeTaggedMember(taggedUserData.id);
                          }}
                          alt="Remove"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
              <Editor
                editorState={editorState}
                onChange={handleEditorChange}
                onBlur={() => {
                  setTimeout(() => setShowMentionDropdown(false), 200);
                }}
                placeholder={`What's on your mind, ${userProfile.full_name.split(" ")[0]}?`}
                editorStyle={{
                  minHeight: "100px",
                  maxHeight: "700px",
                  overflowY: "auto",
                }}
              />

              {showMentionDropdown &&
                createPortal(
                  <div
                    ref={tagDropdownRef}
                    className="d-flex flex-column p-2"
                    style={{
                      position: "fixed",
                      left: dropdownPosition.left,
                      ...(dropdownPosition.placement === "below"
                        ? { top: dropdownPosition.top }
                        : { bottom: dropdownPosition.bottom }),
                      width: dropdownPosition.width,
                      maxHeight: dropdownPosition.maxHeight,
                      overflowY: "auto",
                      zIndex: 99999,
                      backgroundColor: "rgb(28, 42, 55)",
                      border: "1px solid rgb(42, 64, 82)",
                      borderRadius: "10px",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        color: "rgba(255,255,255,0.6)",
                        padding: "6px 8px 8px",
                        borderBottom: "1px solid rgba(42, 64, 82, 0.6)",
                      }}
                    >
                      Mention a group member — type to search
                    </div>

                    {effectiveMembersState?.length > 0 ? (
                      (() => {
                        const alreadyMentioned = getAlreadyMentionedUserIds(
                          editorState.getCurrentContent(),
                        );

                        const filtered = (effectiveMembersState || [])
                          .filter((m) => m.user?.id !== userProfile?.id)
                          .filter(
                            (m) => !alreadyMentioned.has(String(m.user?.id)),
                          )
                          .filter((m) => {
                            const name = (
                              m.user?.full_name || ""
                            ).toLowerCase();
                            return (
                              !mentionSearchQuery ||
                              name.includes(mentionSearchQuery)
                            );
                          });

                        return filtered.length > 0 ? (
                          filtered.map((member) => (
                            <div
                              key={member.user.id}
                              role="button"
                              tabIndex={0}
                              className="d-flex align-items-center gap-2"
                              style={{
                                fontSize: "14px",
                                cursor: "pointer",
                                padding: "8px 10px",
                                borderRadius: "8px",
                                color: "#E6ECF1",
                              }}
                              onClick={() => handleMentionClicked(member)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  handleMentionClicked(member);
                                }
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "rgba(0, 140, 255, 0.15)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "transparent";
                              }}
                            >
                              <img
                                src={member.user.image || "/icons/james.svg"}
                                alt={member.user.full_name}
                                style={{
                                  width: "28px",
                                  height: "28px",
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                  flexShrink: 0,
                                }}
                              />
                              <span
                                style={{ fontWeight: "600", fontSize: "13px" }}
                              >
                                {member.user.full_name}
                              </span>
                            </div>
                          ))
                        ) : (
                          <div
                            style={{
                              fontSize: "13px",
                              color: "rgba(255,255,255,0.6)",
                              padding: "12px 10px",
                            }}
                          >
                            No matching members
                          </div>
                        );
                      })()
                    ) : isLoadingMembers ? (
                      <div
                        style={{
                          fontSize: "13px",
                          color: "rgba(255,255,255,0.6)",
                          padding: "12px 10px",
                        }}
                      >
                        Loading members...
                      </div>
                    ) : (
                      <div
                        style={{
                          fontSize: "13px",
                          color: "rgba(255,255,255,0.6)",
                          padding: "12px 10px",
                        }}
                      >
                        No members to tag
                      </div>
                    )}
                  </div>,
                  document.body,
                )}
            </div>

            {renderFilePreviews()}

            {(!isEditMode || isEditingScheduledPost) && (
              <div
                style={{
                  marginTop: "12px",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  type="button"
                  onClick={openScheduleModal}
                  disabled={!canSubmit}
                  style={{
                    width: "140px",
                    height: "36px",
                    whiteSpace: "nowrap",
                    backgroundColor: "#1C2A37",
                    color: "#C8CED3",
                    border: "1px solid #2A4052",
                    borderRadius: "999px",
                    fontSize: "13px",
                  }}
                >
                  Schedule post
                </button>
              </div>
            )}
          </>
        )}

        {showScheduleModal && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                backgroundColor: "#111827",
                borderRadius: "24px",
                padding: "24px",
                minWidth: "460px",
                maxWidth: "560px",
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.6)",
                color: "#F9FAFB",
              }}
            >
              <h4
                style={{
                  margin: 0,
                  marginBottom: "16px",
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#F9FAFB",
                }}
              >
                Select date and time
              </h4>

              <div style={{ display: "flex", gap: "16px" }}>
                <div
                  style={{
                    flex: "0 0 240px",
                    borderRadius: "16px",
                    padding: "14px",
                    backgroundColor: "#0B1120",
                    border: "1px solid #1F2937",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setCalendarMonth((prev) => {
                          const d = new Date(prev);
                          d.setMonth(d.getMonth() - 1);
                          return new Date(d.getFullYear(), d.getMonth(), 1);
                        });
                      }}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#9CA3AF",
                        cursor: "pointer",
                        fontSize: "18px",
                      }}
                    >
                      ‹
                    </button>
                    <span style={{ fontSize: "14px", fontWeight: 500 }}>
                      {calendarMonth.toLocaleDateString(undefined, {
                        month: "long",
                        year: "numeric",
                      })}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setCalendarMonth((prev) => {
                          const d = new Date(prev);
                          d.setMonth(d.getMonth() + 1);
                          return new Date(d.getFullYear(), d.getMonth(), 1);
                        });
                      }}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#9CA3AF",
                        cursor: "pointer",
                        fontSize: "18px",
                      }}
                    >
                      ›
                    </button>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(7, 1fr)",
                      fontSize: "11px",
                      color: "#6B7280",
                      marginBottom: "4px",
                    }}
                  >
                    {["M", "T", "W", "T", "F", "S", "S"].map((d) => (
                      <div
                        key={d}
                        style={{ textAlign: "center", padding: "4px 0" }}
                      >
                        {d}
                      </div>
                    ))}
                  </div>

                  {(() => {
                    const firstDay = new Date(
                      calendarMonth.getFullYear(),
                      calendarMonth.getMonth(),
                      1,
                    );
                    const startOffset = (firstDay.getDay() + 6) % 7;
                    const daysInMonth = new Date(
                      calendarMonth.getFullYear(),
                      calendarMonth.getMonth() + 1,
                      0,
                    ).getDate();

                    const today = getNowLocal();
                    today.setHours(0, 0, 0, 0);

                    const cells = [];
                    const totalCells = startOffset + daysInMonth;

                    const selected = scheduledDate
                      ? new Date(scheduledDate)
                      : null;

                    for (let i = 0; i < totalCells; i += 1) {
                      if (i < startOffset) {
                        cells.push(<div key={`empty-${i}`} />);
                      } else {
                        const day = i - startOffset + 1;
                        const thisDate = new Date(
                          calendarMonth.getFullYear(),
                          calendarMonth.getMonth(),
                          day,
                        );
                        thisDate.setHours(0, 0, 0, 0);

                        const isPast = thisDate < today;

                        const isSelected =
                          !isPast &&
                          selected &&
                          thisDate.getFullYear() === selected.getFullYear() &&
                          thisDate.getMonth() === selected.getMonth() &&
                          thisDate.getDate() === selected.getDate();

                        cells.push(
                          <button
                            key={day}
                            type="button"
                            disabled={isPast}
                            onClick={() => {
                              if (isPast) return;
                              const y = thisDate.getFullYear();
                              const m = thisDate.getMonth() + 1;
                              const d = thisDate.getDate();
                              const iso = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
                              setScheduledDate(iso);
                            }}
                            style={{
                              width: "100%",
                              height: "32px",
                              borderRadius: "999px",
                              border: "none",
                              cursor: isPast ? "not-allowed" : "pointer",
                              fontSize: "13px",
                              color: isSelected
                                ? "#0B1120"
                                : isPast
                                  ? "#4B5563"
                                  : "#E5E7EB",
                              backgroundColor: isSelected
                                ? "#3B82F6"
                                : "transparent",
                              opacity: isPast ? 0.3 : 1,
                              outline: "none",
                            }}
                          >
                            {day}
                          </button>,
                        );
                      }
                    }

                    return (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(7, 1fr)",
                          gap: "2px",
                          marginTop: "4px",
                        }}
                      >
                        {cells}
                      </div>
                    );
                  })()}
                </div>

                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "12px",
                        marginBottom: "4px",
                        color: "#9CA3AF",
                      }}
                    >
                      Date
                    </label>

                    <div style={{ position: "relative", width: "100%" }}>
                      <input
                        type="text"
                        readOnly
                        value={
                          scheduledDate
                            ? new Date(scheduledDate).toLocaleDateString(
                                undefined,
                                {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                },
                              )
                            : ""
                        }
                        placeholder="Select a date from calendar"
                        style={{
                          width: "100%",
                          padding: "8px 36px 8px 10px",
                          borderRadius: "8px",
                          border: "1px solid #374151",
                          fontSize: "14px",
                          backgroundColor: "#020617",
                          color: "#F9FAFB",
                        }}
                      />
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                        }}
                      >
                        <rect
                          x="3"
                          y="4"
                          width="18"
                          height="17"
                          rx="2"
                          stroke="#FFFFFF"
                          strokeWidth="1.5"
                        />
                        <path
                          d="M8 2V6"
                          stroke="#FFFFFF"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M16 2V6"
                          stroke="#FFFFFF"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M3 9H21"
                          stroke="#FFFFFF"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "12px",
                        marginBottom: "4px",
                        color: "#9CA3AF",
                      }}
                    >
                      Time
                    </label>

                    <input
                      type="time"
                      className="custom-time"
                      value={scheduledTime}
                      min={timeMin}
                      onChange={(e) => {
                        const next = e.target.value;

                        if (
                          scheduledDate === todayIso &&
                          next &&
                          next < currentTimeHHMM
                        ) {
                          toast.error("Please select a future time.");
                          setScheduledTime(currentTimeHHMM);
                          return;
                        }

                        setScheduledTime(next);
                      }}
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        borderRadius: "8px",
                        border: "1px solid #374151",
                        fontSize: "14px",
                        backgroundColor: "#020617",
                        color: "#F9FAFB",
                      }}
                    />
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "12px",
                  marginTop: "24px",
                }}
              >
                <button
                  type="button"
                  onClick={closeScheduleModal}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#60A5FA",
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleScheduleConfirm}
                  disabled={!canSubmit}
                  style={{
                    minWidth: "130px",
                    padding: "8px 16px",
                    borderRadius: "24px",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#ffffff",
                    backgroundColor: "#1D4ED8",
                    opacity: !canSubmit ? 0.6 : 1,
                  }}
                >
                  Schedule post
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatePostCard;
